"use client";

/**
 * 後台已登入殼層：AppShell + 主選單（src/nav/menu.ts）+ 使用者資訊（GET /me）。
 * 未帶 token 時導回 /login（副作用；SSR/首次繪製仍會輸出完整選單 DOM，
 * 不影響 curl 驗收看到選單詞條）。
 *
 * 第 1 輪修正（任務指示第 3 條）：依登入者權限閉包過濾選單，僅渲染有權限的
 * 群組／項目（比照舊站 left.php 的 $identity->can(...)）。規則見 filterNavByPermissions。
 *
 * WP-A3（B2 批）：選單過濾**不再是唯一閘門**。
 * - 判斷邏輯全數收斂到 lib/permissions.ts 的 can()（含 Super Administrator 的唯一
 *   bypass 點），本檔不再自己組 Set、也不再自己判斷超管。
 * - children 包在 <RequirePermission> 裡，直接打網址而沒有該頁權限者看到 403 頁而非
 *   內容（舊站 AccessControl.php 的語意）。GET /me 只在本檔取一次，經
 *   <PermissionProvider> 供給整棵子樹，守衛與頁面都不會再各發一次請求。
 */
import { useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AppShell, Button, ThemeToggle, type NavItem, type NavSpec } from "@ccheng/ui";
import Link from "next/link";
import type { ReactNode } from "react";
import { adminNav } from "../nav/menu";
import { authApi, getToken } from "../lib/apiClient";
import { useCurrentUser } from "../lib/useCurrentUser";
import { can } from "../lib/permissions";
import { PermissionProvider, RequirePermission } from "./RequirePermission";
import { NextNavLink } from "./NextNavLink";
import type { PermissionSubject } from "../lib/permissions";
import type { MeResponse } from "../lib/apiClient";

/**
 * 依權限閉包過濾選單（規格 §4.4；比照 legacy left.php visible => $identity->can(...)）：
 * - me 尚未載入（載入中／無 token）：不過濾，維持 SSR/首屏輸出完整選單 DOM。
 *   這一條是**顯示層的取捨**，不是閘門——選單看得到也進不去，路由層由
 *   <RequirePermission> 把關（且它在權限確定前不畫任何內容）。
 * - Super Administrator（或權限閉包含萬用 "*"）：can() 內部單點 bypass，全通配。
 * - 其餘：僅保留「未標 permission（尚未建置或本就免權限的入口）」或「can() 成立」
 *   的項目；群組內全部項目被濾掉則整個群組隱藏。
 * - 巢狀子群組標題（無 href、僅 children，如 g10「3. 後端-權限管理」）：遞迴過濾
 *   children，任一子項可見則標題可見、全滅則整個標題隱藏——比照 legacy left.php
 *   對子群組 visible 取子項權限 OR 的作法（第 2 輪修正，任務指示第 5、6 條）。
 *
 * WP-A3：這裡不再自己組權限 Set、也不再自己判斷超管——判斷式只有 can() 一個，
 * 與路由守衛消費的是同一支函式，選單與路由不可能各說各話。
 */
function filterItems(items: NavItem[], subject: PermissionSubject): NavItem[] {
  return items
    .map((item) => {
      if (item.children?.length) {
        const children = filterItems(item.children, subject);
        return children.length > 0 ? { ...item, children } : null;
      }
      return !item.permission || can(subject, item.permission) ? item : null;
    })
    .filter((item): item is NavItem => item !== null);
}

export function filterNavByPermissions(nav: NavSpec, me: MeResponse | undefined): NavSpec {
  if (!me) return nav;
  return nav
    .map((group) => ({
      ...group,
      items: filterItems(group.items, me),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * 由目前路徑推導頂欄麵包屑「群組 › 項目」。
 *
 * 改版前這裡是寫死的 `title="網站管理"` —— 不論在哪一頁都顯示同一句，
 * 60 幾個非網站管理的頁面全部顯示錯的位置。
 *
 * 比對方式刻意用**路徑前綴**而非字串等值：menu.ts 有 9 個項目靠 query 參數區分
 * （如 `?view=genealogy`、`?type=1`），而 usePathname() 不含 query，直接比會全部落空。
 * 取最長匹配，避免 `/a` 蓋過 `/a/b`。
 */
function breadcrumbOf(nav: NavSpec, pathname: string | null): string | undefined {
  if (!pathname) return undefined;
  let best: { len: number; text: string } | undefined;
  const walk = (items: NavItem[], group: string, trail: string[]) => {
    for (const it of items) {
      if (it.href) {
        const base = it.href.split("?")[0];
        if ((pathname === base || pathname.startsWith(base + "/")) && base.length > (best?.len ?? 0)) {
          best = { len: base.length, text: [group, ...trail, it.label].filter(Boolean).join(" › ") };
        }
      }
      if (it.children?.length) walk(it.children, group, [...trail, it.label]);
    }
  };
  for (const g of nav) walk(g.items, g.label ?? "", []);
  return best?.text;
}

export function AdminShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { currentUser, me, loading } = useCurrentUser();

  const visibleNav = useMemo(() => filterNavByPermissions(adminNav, me), [me]);
  // 麵包屑用**完整**選單推導而非過濾後的：使用者若因權限看不到某群組，仍應在
  // 該頁看到正確的位置標示（能開到那一頁本身就代表有權限，路由層另有把關）。
  const breadcrumb = useMemo(() => breadcrumbOf(adminNav, pathname), [pathname]);

  useEffect(() => {
    if (!getToken()) {
      router.replace("/login");
    }
  }, [router]);

  function handleLogout() {
    authApi.logout();
    router.replace("/login");
  }

  return (
    <PermissionProvider subject={me} loading={loading}>
      <AppShell
        nav={visibleNav}
        activeHref={pathname ?? undefined}
        // 品牌標記：金色圓角方塊 + 「心」。設計樣板一直有這個圖示，但實作時只傳了
        // 純文字，導致側欄左上角與樣板對不起來（業主 2026-09-02 指出）。
        // 側欄收合成 64px icon rail 時，這個方塊是唯一還看得見的品牌識別。
        brand={
          <>
            <span className="cui-shell-brand-mark" aria-hidden>
              心
            </span>
            <span>心成後台</span>
          </>
        }
        title={breadcrumb}
        user={currentUser}
        linkComponent={NextNavLink}
        topbarActions={
          <>
            {/* F5a 補做：舊站 `admin/user/profile`（改自己的帳號/密碼/姓名/區域）
                新後台原本右上角只有「登出」，沒有任何入口——見 b2_browser_功能.md
                「個人資料 /admin/user/profile ... 新後台無對應頁」。這裡加回去，
                免權限節點（舊站 allowActions，任何已登入帳號都能開）。 */}
            <Link href="/profile">
              <Button variant="ghost">個人資料</Button>
            </Link>
            {/* 明/暗/跟隨系統三態切換（業主 2026-09-02 要求）。放在登出鈕之前，
                讓破壞性最低的操作離使用者最遠。 */}
            <ThemeToggle />
            <Button variant="ghost" onClick={handleLogout}>
              登出
            </Button>
          </>
        }
      >
        <RequirePermission>{children}</RequirePermission>
      </AppShell>
    </PermissionProvider>
  );
}
